import { useIntl } from '@/intl'
import { useCampaignDetails } from '@/scopes/search/search/domain/campaign-details/hooks'
import { space, tok } from '@sunwebgroup/ui-components'
import { Icon, IndeterminateProgressCircle, Sheet, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'

interface CampaignDetailsProps {
  id: string
  title: string
}

const CampaignInfoButton = styled.button`
  margin-left: ${space('primitives_space_8')};
  position: relative;

  ${Icon} {
    vertical-align: middle;
  }
`

const ErrorMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${space('primitives_space_12')};

  text-align: center;
  margin: 0 ${space('primitives_space_16')};
  color: ${tok('semantics_content_secondary_color')};
`

const CampaignDetailsContent = styled(Typography)`
  padding: ${space('primitives_space_16')};
`

export function CampaignDetails(props: CampaignDetailsProps) {
  const [open, setOpen] = React.useState(false)

  return (
    <Sheet.Root open={open} onOpenChange={setOpen}>
      <Sheet.Trigger>
        <CampaignInfoButton
          onClick={(ev: React.MouseEvent<HTMLButtonElement>) => {
            setOpen(true)
            ev.preventDefault()
          }}
        >
          <Icon glyph="InformationInCircle" size={16} />
        </CampaignInfoButton>
      </Sheet.Trigger>
      <CampaignDetailsResult {...props} />
    </Sheet.Root>
  )
}

function CampaignDetailsResult({ id, title }: CampaignDetailsProps) {
  const intl = useIntl()
  const { isLoading, error, campaignDetails } = useCampaignDetails(id)

  return (
    <Sheet.Body side="right">
      <Sheet.Header titleLabel={title} closeLabel={intl.formatMessage({ id: 'close' })} />
      <Sheet.Content>
        {isLoading && <IndeterminateProgressCircle />}
        {error && (
          <ErrorMessage>
            <Icon glyph="ExclamationInTriangle" />
            <Typography as="p" $variant="body-l-bold-snug">
              {intl.formatMessage({ id: 'campaign_details_error_msg' })}
            </Typography>
          </ErrorMessage>
        )}
        {campaignDetails && (
          <CampaignDetailsContent
            as="div"
            $variant="body-m-regular-snug"
            dangerouslySetInnerHTML={{ __html: campaignDetails.content }}
          />
        )}
      </Sheet.Content>
      {' '}
    </Sheet.Body>
  )
}
